// Environment safety utilities to prevent SSR and browser compatibility errors

export const isBrowser = typeof window !== 'undefined';
export const isNode = typeof globalThis !== 'undefined' && typeof (globalThis as any).process !== 'undefined';

// Safe access to window object
export const safeWindow = isBrowser ? window : ({} as Window);

// Safe access to navigator object
export const safeNavigator = isBrowser && typeof navigator !== 'undefined' ? navigator : ({} as Navigator);

// Safe access to performance object
export const safePerformance = isBrowser && typeof performance !== 'undefined' ? performance : null;

// Safe access to process.env
export const safeProcessEnv = (() => {
  try {
    if (isNode && (globalThis as any).process?.env) {
      return (globalThis as any).process.env;
    }
  } catch {
    // Ignore
  }
  return {};
})();

// Safe feature detection
export const hasFeature = {
  performanceObserver: isBrowser && 'PerformanceObserver' in safeWindow,
  performanceMemory: safePerformance && 'memory' in safePerformance,
  networkInformation: safeNavigator && 'connection' in safeNavigator,
  intersectionObserver: isBrowser && 'IntersectionObserver' in safeWindow,
  mutationObserver: isBrowser && 'MutationObserver' in safeWindow,
};

// Safe environment check using Vite's import.meta.env
export const isDevelopment = () => {
  try {
    // Use Vite's environment variable for browser builds
    if (isBrowser && typeof import.meta !== 'undefined' && import.meta.env) {
      return import.meta.env.MODE === 'development' || import.meta.env.DEV === true;
    }
    // Fallback for Node.js environment
    return safeProcessEnv.NODE_ENV === 'development';
  } catch {
    return false;
  }
};

export const isProduction = () => {
  try {
    // Use Vite's environment variable for browser builds
    if (isBrowser && typeof import.meta !== 'undefined' && import.meta.env) {
      return import.meta.env.MODE === 'production' || import.meta.env.PROD === true;
    }
    // Fallback for Node.js environment
    return safeProcessEnv.NODE_ENV === 'production';
  } catch {
    return true; // Default to production for safety
  }
};

// Safe callback wrapper
export const safeCallback = <T extends (...args: any[]) => any>(
  callback: T,
  fallback?: () => void
): T => {
  return ((...args: Parameters<T>) => {
    try {
      return callback(...args);
    } catch (error) {
      console.warn('Safe callback error:', error);
      fallback?.();
    }
  }) as T;
};